import { useDispatch, useSelector } from 'react-redux';

import {
	InvitedPresenter,
	Session,
} from '../components/app-entry/producer/meeting-modal';
import * as Alerts from '../components/general-components/alert-service';
import {
	CheckJoinRequest,
	CheckWatcherOnly,
	GetIsLive,
	GetRequiresApproval,
	JoinRequest,
} from '../connection/join-request';
import {
	CreateOrUpdateMeeting,
	LoadMeeting,
} from '../connection/meeting';
import * as InvitationActions from '../stores/actions/invitations';
import * as SessionActions from '../stores/actions/session';
import * as VideoActions from '../stores/actions/video';
import { Meeting } from '../stores/reducers/session';
import { GreenroomState } from '../stores/types/types';
import {shouldEnableUserMedia} from "../utils/utils";

export interface MeetingExtended extends Meeting {
	live_chat?: number | string;
}

interface UpsertMeetingOptions {
	standaloneTitle?: string;
	invites?: InvitedPresenter[];
	sessions?: Session[];
	showroomsToken?: string;
	meetingId?: string;
}

interface LoadMeetingOptions {
	showroomsToken?: string;
	selectedApp?: string;
}

interface useJoinReturn {
	getCurrentOrNewMeeting: (
		token: string,
		meetingObject: Partial<MeetingExtended>,
		appSignedInWith: string,
		upsertMeetingObject?: UpsertMeetingOptions,
		loadMeetingObject?: LoadMeetingOptions,
	) => Promise<Partial<MeetingExtended> | null>
	enterGreenroom: (meeting: any) => Promise<void>;
	joinGreenroom: (
		meeting: Meeting | null,
		fromRow?: boolean | undefined
	) => Promise<boolean>;
}

const useJoin = (): useJoinReturn => {
	const dispatch = useDispatch();
	const token = useSelector(
		(state: GreenroomState) => state.SessionReducer.token,
	);
	const appSignedInWith = useSelector(
		(state: GreenroomState) => state.SessionReducer.appSignedInWith,
	);
	const host = useSelector(
		(state: GreenroomState) => state.SessionReducer.host,
	);
	const restrictedUserState = useSelector(
		(state: GreenroomState) => state.SessionReducer.restrictedUserState,
	);
	const mainVideoStream = useSelector(
		(state: GreenroomState) => state.VideoReducer.mainVideoStream,
	);
	const liveChannels = useSelector(
		(state: GreenroomState) => state.ContentReducer.liveChannels,
	);

	const getCurrentOrNewMeeting = async (
		// eslint-disable-next-line @typescript-eslint/no-shadow
		token: string,
		meetingObject: Partial<MeetingExtended>,
		// eslint-disable-next-line @typescript-eslint/no-shadow
		appSignedInWith: string,
		upsertMeetingObject: UpsertMeetingOptions = {},
		loadMeetingObject: LoadMeetingOptions = {},
	): Promise<Partial<MeetingExtended> | null> => {
		// This should not happen, but to ensure no surprises with how it is currently set up.
		if (!meetingObject) {
			return null;
		}

		const currentMeetingNumber = meetingObject.meeting;

		if (currentMeetingNumber) {
			const existingMeeting: Partial<MeetingExtended> | null | undefined = await LoadMeeting(
				token,
				currentMeetingNumber,
			);

			if (existingMeeting) {
				return existingMeeting;
			}
		}

		// Note moved from below:
		// For Events Legacy, the id may be brandlive_event instead of
		// live_chat depending on where this function is called.
		const streamsId = meetingObject?.events_session_uuid;
		const legacyId = meetingObject?.live_chat ?? meetingObject?.brandlive_event;
		const appSpecificMeetingId: string | number | null | undefined = streamsId ?? legacyId;

		// Once again ensuring no surprises with typings as they currently are.
		if (!appSignedInWith) {
			return null;
		}

		if (appSpecificMeetingId) {
			// Okay if destructured values undefined
			const {
				standaloneTitle,
				invites,
				sessions,
				showroomsToken,
				meetingId,
			} = upsertMeetingObject;
			const newMeeting: Partial<MeetingExtended> | null | undefined = await CreateOrUpdateMeeting(
				token,
				appSpecificMeetingId.toString(),
				meetingObject,
				appSignedInWith,
				standaloneTitle,
				invites,
				sessions,
				showroomsToken,
				meetingId,
			);

			if (newMeeting?.meeting) {
				// Okay if destructured values undefined
				const {
					showroomsToken: loadMeetingShowroomsToken,
					selectedApp,
				} = loadMeetingObject;
				const newMeetingToLoad: Partial<MeetingExtended> | null | undefined = await LoadMeeting(
					token,
					newMeeting.meeting,
					loadMeetingShowroomsToken,
					selectedApp,
				);

				if (newMeetingToLoad) {
					return newMeetingToLoad;
				}
			}
		}

		return null;
	};

	const enterGreenroom = async (
		meeting: Partial<MeetingExtended> | null | undefined,
	): Promise<void> => {
		if (!token || !meeting) {
			return;
		}

		/*
			For all of our apps except Standalone GR when a producer clicks to
			join they are either creating a new meeting that doesn't exist or
			updating an existing meeting to keep necessary fields up to date
			for things like rehearsal emails, etc. Standalone GRs don't create
			or update a meeting here because each row is the actual meeting
			already and presenters/watchers don't create or update a meeting
			because it will already exist for them.
		*/
		let selectedMeeting: Partial<MeetingExtended> | null = meeting;

		if (!meeting.meeting) {
			selectedMeeting = await getCurrentOrNewMeeting(
				token,
				meeting,
				appSignedInWith,
			);
		}

		const selectedMeetingNumber = selectedMeeting?.meeting;

		if (!selectedMeetingNumber) {
			return;
		}

		const loadedMeeting = await LoadMeeting(token, selectedMeetingNumber);

		if (loadedMeeting?.meeting_id) {
			window.location.href = `${host ? '/producer' : ''}/${loadedMeeting.meeting_id}`;
		}
	};

	const joinGreenroom = async (
		meeting: Meeting | null,
		fromRow?: boolean,
	): Promise<boolean> => {
		if (!meeting) {
			return false;
		}

		if (host) {
			enterGreenroom(meeting);

			return true;
		}
		const meetingWithOtherProp = meeting as any;
		if (
			!meetingWithOtherProp.entry_without_producer
				&& !liveChannels?.includes(meeting.meeting)
		) {
			return false;
		}

		try {
			dispatch(SessionActions.setSelectedEvent(meeting));
			// check if the event is currently live and requires approval
			// eslint-disable-next-line camelcase
			const { is_live } = token && (await GetIsLive(token, meeting.meeting));
			// eslint-disable-next-line camelcase
			const { approval_required } = token
				&& (await GetRequiresApproval(token, meeting.meeting));

			let checkUser: any = {};

			if (token) {
				// eslint-disable-next-line camelcase
				if (approval_required) {
					checkUser = await CheckJoinRequest(
						token,
						meeting.meeting,
					);
				} else {
					checkUser = await CheckWatcherOnly(
						token,
						meeting.meeting,
					);
					console.log('set watcher only');
					dispatch(
						SessionActions.setWatcherOnly(
							!!checkUser.watcher_only,
						),
					);
				}
			}

			// self mute if live but not if watcher
			// eslint-disable-next-line camelcase
			if (is_live && !checkUser.watcher_only) {
				dispatch(VideoActions.setAudioMute(true));
			}

			// if approved or approval is not required, load the invitations and
			// set onboarding to complete to load the main UI
			// eslint-disable-next-line camelcase
			if (checkUser.approved || !approval_required) {
				enterGreenroom(meeting);

				return true;
			}
			// if not approved, send a join request and wait
			// eslint-disable-next-line camelcase
			const { join_request } = token && (await JoinRequest(token, meeting.meeting));

			dispatch(
				InvitationActions.setJoinRequest(join_request),
			);

			if (!fromRow) {
				dispatch(
					SessionActions.setAwaitingProducer(true),
				);
			} else {
				Alerts.showAlert({
					message: 'Waiting for producer',
					description: "You'll join when the producer lets you in",
					duration: 2000,
					type: 'neutral',
				});

				return false;
			}

			return true;
		} catch (e) {
			console.error(e);

			Alerts.showAlert({
				message: 'Unable to Join',
				description: 'We ran into an issue checking permissions to join the greenroom. Please try again.',
				type: 'error',
			});

			return false;
		}
	};

	return {
		getCurrentOrNewMeeting,
		enterGreenroom,
		joinGreenroom,
	};
};

export default useJoin;
